import React from "react";
//#region types
import { WintSuperkollAPIModel, WintUtilsWebApi } from "wint-api-client/dist/types";
import { SupportIssueListExtended, SupportIssuesListProps } from "models/propTypes/supportPropTypes";
import { TranslationKeys } from "translations";
import { User } from "models/propTypes/userProfilePropTypes";
import { SupportFormTypes } from "models/formTypes";
import { TableConstants } from "utils/constants";
//#endregion
//#region components
import { Grid } from "@mui/material";
import { TableAsync, useFilterContext, useSearchContext } from "components/common";
import SupportStatusTags from "./statusTags";
import SupportIssuesListMessage from "./_partials/issuesListMessage";
import { useSupportService } from "serviceHooks";
import { MultiProfiles } from "components/common/profile";
//#endregion
import { DateHelper, LocalizationHelperInstance, UrlHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const SupportIssuesList: React.FC<SupportIssuesListProps> = (props) => {
  const supportService = useSupportService();
  const { filter } = useFilterContext<SupportFormTypes.Filter>();
  const searchContext = useSearchContext();

  const filterFunc = React.useCallback(
    (baseFilter: WintUtilsWebApi.Filter) => {
      const updatedFilter = { ...baseFilter };

      if (baseFilter.orderByProperty !== "StateChangedTimestamp") {
        updatedFilter.thenByProperty = "StateChangedTimestamp";
        updatedFilter.thenByDescending = true;
      }
      return supportService.SupportIssueListItemFilter(updatedFilter, {
        ...filter,
        searchString: searchContext?.searchText,
      });
    },
    [filter, searchContext?.searchText]
  );

  const getDisplayDate = (date: Date) => {
    const lastUpdatedUTC = DateHelper.getUtcDate(DateHelper.convertUTCDateToLocalDate(new Date(date)));
    const nowUTC = DateHelper.getUtcDate(new Date());

    if (DateHelper.getDiffDays(lastUpdatedUTC, nowUTC) === 0) {
      const endPhrase = " " + LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Ago);
      return DateHelper.formatDateDistance(lastUpdatedUTC, nowUTC, endPhrase);
    } else {
      return new Date(date);
    }
  };

  const getItemUrl = (id: number | string) => UrlHelper.formatPath(`/Support/${id}`);

  return (
    <>
      <Grid container>
        <TableAsync<SupportIssueListExtended>
          tableId={TableConstants.SupportList}
          properties={{
            lastCommentedUser: {
              description: "",
              formatter: (supportTicket: WintSuperkollAPIModel.SupportIssueListItem) => {
                return new User(
                  supportTicket.latestComment?.fromWint ? undefined : supportTicket.latestComment?.user?.id,
                  supportTicket.latestComment?.user?.name,
                  true
                );
              },
              alignment: "center",
              width: "56px",
              disableSort: true,
            },
            title: {
              description: TranslationKeys.Common.Subject,
              formatter: (supportTicket: WintSuperkollAPIModel.SupportIssueListItem) => (
                <SupportIssuesListMessage
                  isUnread={supportTicket.isUnread}
                  title={supportTicket.title}
                  latestMessage={supportTicket.latestComment?.message}
                />
              ),
            },
            user: {
              description: TranslationKeys.Common.User,
              formatter: (supportTicket: WintSuperkollAPIModel.SupportIssueListItem) => {
                if (supportTicket.participants) {
                  if (supportTicket.participants.length > 1) {
                    return (
                      <MultiProfiles
                        maxProfiles={supportTicket.participants.length > 5 ? 4 : undefined}
                        profiles={supportTicket.participants?.map((p) => ({ id: p.id, name: p.name ?? "" }))}
                      />
                    );
                  } else {
                    const user = supportTicket.participants[0] ?? supportTicket.user;
                    return new User(user.id, user.name, false);
                  }
                }
                return "";
              },
              width: "200px",
              alignment: "left",
              disableSort: true,
            },
            stateChangedTimestamp: {
              description: TranslationKeys.Common.LastModified,
              formatter: (supportTicket: WintSuperkollAPIModel.SupportIssueListItem) =>
                getDisplayDate(supportTicket.stateChangedTimestamp),
              width: "200px",
              classification: "date",
            },
            importanceOrder: {
              description: TranslationKeys.Common.Status,
              formatter: (supportTicket: WintSuperkollAPIModel.SupportIssueListItem) => (
                <SupportStatusTags
                  status={supportTicket.state}
                  isLocked={supportTicket.isLocked}
                  isOwnedByAgency={supportTicket.isOwnedByAgency}
                  isViewerAgencyMember={supportTicket.isViewerAgencyMember}
                />
              ),
              width: "100px",
              classification: "number",
            },
            group: {
              formatter: (supportTicket: WintSuperkollAPIModel.SupportIssueListItem) => {
                if (
                  supportTicket.state === WintSuperkollAPIModel.SupportIssueState.AtCustomer ||
                  supportTicket.state === WintSuperkollAPIModel.SupportIssueState.AtSupport
                )
                  return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SupportIssue.OpenCases);
                else return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SupportIssue.ClosedCases);
              },
            },
          }}
          filter={filterFunc}
          maxRowCount={10}
          pagination="advanced"
          orderByProperty={"importanceOrder"}
          orderByDescending={false}
          itemUrl={getItemUrl}
          className={cssExports.supportIssuesList}
          reloadKey={props.reloadKey}
          disableGroupWhenOrderBy={["user", "title", "stateChangedTimestamp"]}
        />
      </Grid>
    </>
  );
};

export default SupportIssuesList;
